import type { UseQueryResult } from '@tanstack/react-query';
import type { ApiEndpoints } from '@avisoma/shared';
import { DescriptionList, DescriptionTerm, DescriptionDetails } from '../../catalyst/description-list';
import { Link } from '../../components/Link';
import FileSize from '../../components/FileSize';
import { formatDateTime } from '../../lib/dateFormat';
import { SectionCard } from './SectionCard';
import { QueryStateGuard } from './QueryStateGuard';

type UploadQueryResult = UseQueryResult<ApiEndpoints<string>['/api/uploads/:id']['GET']['response']>;

export function UploadDetailsSection({ uploadQuery }: { uploadQuery: UploadQueryResult }) {
  return (
    <QueryStateGuard query={uploadQuery} noDataMessage="No upload found.">
      {(data) => {
        const upload = data.item;
        if (!upload) {
          return <div className="text-sm text-gray-500 dark:text-zinc-400">No upload found.</div>;
        }
        return (
          <SectionCard title="Upload Details">
            <DescriptionList>
              <DescriptionTerm>ID</DescriptionTerm>
              <DescriptionDetails>{upload.id}</DescriptionDetails>

              <DescriptionTerm>Uploaded By</DescriptionTerm>
              <DescriptionDetails>{upload.username}</DescriptionDetails>

              <DescriptionTerm>Uploaded At</DescriptionTerm>
              <DescriptionDetails>
                {upload.createdAt ? formatDateTime(upload.createdAt) : 'N/A'}
              </DescriptionDetails>

              <DescriptionTerm>Document Count</DescriptionTerm>
              <DescriptionDetails>{data.documents?.length}</DescriptionDetails>

              <DescriptionTerm>Page Count</DescriptionTerm>
              <DescriptionDetails>
                {data.documents?.reduce((acc, row) => acc + row.pageCount, 0)}
              </DescriptionDetails>

              <DescriptionTerm>Page Bytes</DescriptionTerm>
              <DescriptionDetails>
                <FileSize bytes={data.documents?.reduce((acc, row) => acc + row.pageBytes, 0) ?? 0} />
              </DescriptionDetails>

              <DescriptionTerm>Project ID</DescriptionTerm>
              <DescriptionDetails>
                {upload.projectId ? (
                  <Link to={`/projects/${upload.projectId}`}>{upload.projectId}</Link>
                ) : (
                  'N/A'
                )}
              </DescriptionDetails>

              {upload.organisation && (
                <>
                  <DescriptionTerm>Organisation</DescriptionTerm>
                  <DescriptionDetails>
                    <Link
                      to={`/organisations/${encodeURIComponent(upload.organisation.id)}`}
                      className="text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      {upload.organisation.name}
                    </Link>
                  </DescriptionDetails>
                </>
              )}
            </DescriptionList>
          </SectionCard>
        );
      }}
    </QueryStateGuard>
  );
}
